import type { GridApi, GridFilterModel, GridRowSelectionModel } from '@repo/go-table';
import { hasValue } from '@repo/utils';
import { format, fromUnixTime } from 'date-fns';
import { action, computed, flow, makeObservable, observable, reaction, runInAction, toJS } from 'mobx';
import type { MutableRefObject } from 'react';

import type { TOption } from 'src/features/well-form/types';
import i18n from 'src/i18n';
import type { WellsService } from 'src/services';
import type { TTableSettingsModel } from 'src/services/user-settings-service/types';
import type { IUserSettingsService } from 'src/services/user-settings-service/user-settings-service';
import type { TheWellEntity } from 'src/services/wells-service/entities';
import { DEFAULT_TABLE_SETTINGS_MODEL } from 'src/shared/constants/mui';
import { createPromiseController } from 'src/shared/utils/promise-controller';
import type { Directories } from 'src/store/directories/directories.store';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import type { PlanVersionsService } from 'src/store/plan-version/plan-versions-service';

import type { WellsPageStore } from '../../wells-page.store';

import { DisplayManager } from './table-settings/display-manager';
import { applySettingsToTable, isWellUnplanned } from './utils';

export const EXCEL_WIDTH_UNIT_PX_RATIO = 7.25;

export class WellsTableStore {
  private readonly wellsService: WellsService;
  private readonly apiRef: MutableRefObject<GridApi>;
  private readonly tableApiRefForExport: MutableRefObject<GridApi>;
  private readonly userSettingsService: IUserSettingsService;
  private readonly notifications: NotificationsStore;
  private readonly wellsPageStore: WellsPageStore;
  private readonly planVersionService: PlanVersionsService;
  private readonly directories: Directories;

  @observable displayManager: DisplayManager | null = null;
  @observable defaultColumnsOrderModel: string[] = [];
  @observable selectedRows: GridRowSelectionModel = [];

  constructor(
    wellsPageStore: WellsPageStore,
    wellsService: WellsService,
    planVersionService: PlanVersionsService,
    userSettingsService: IUserSettingsService,
    notifications: NotificationsStore,
    apiRef: MutableRefObject<GridApi>,
    tableApiRefForExport: MutableRefObject<GridApi>,
    directories: Directories,
  ) {
    this.wellsPageStore = wellsPageStore;
    this.wellsService = wellsService;
    this.userSettingsService = userSettingsService;
    this.apiRef = apiRef;
    this.notifications = notifications;
    this.tableApiRefForExport = tableApiRefForExport;
    this.planVersionService = planVersionService;
    this.directories = directories;

    makeObservable(this);
  }

  @computed
  get blockOptions(): TOption[] {
    return this.wellsService.blocks.map((block) => ({
      label: block.data['name']?.toString() ?? '',
      value: block.id as number,
    }));
  }
  @computed
  get fieldOptions(): TOption[] {
    return this.wellsService.fields.map((field) => ({
      label: field.data['name']?.toString() ?? '',
      value: field.id as number,
    }));
  }
  @computed
  get organizationOptions(): TOption[] {
    const organizationActivities = this.directories.getObject('Common_OrganizationActivity');

    if (!organizationActivities || !organizationActivities.length) {
      return this.wellsService.organizations.map((organization) => ({
        label: organization.data['name']?.toString() ?? '',
        value: organization.id as number,
      }));
    }

    const subsoilUserActivityId = organizationActivities.find((oa) => oa.data['slug'] === 'suboil-user')?.id;

    if (!hasValue(subsoilUserActivityId)) {
      return this.wellsService.organizations.map((organization) => ({
        label: organization.data['name']?.toString() ?? '',
        value: organization.id as number,
      }));
    }

    return this.wellsService.organizations

      .filter((organization) => organization.data['organizationActivityId'] === subsoilUserActivityId)
      .map((organization) => ({
        label: organization.data['name']?.toString() ?? '',
        value: organization.id as number,
      }));
  }

  @computed
  get padOptions(): TOption[] {
    return this.wellsService.pads.map((pad) => ({
      label: pad.data['abbreviatedName']?.toString() ?? '',
      value: pad.id as number,
    }));
  }

  @computed
  get fluidTypeOptions(): TOption[] {
    return this.wellsService.fluidTypes.map((fluidType) => ({
      label: fluidType.data['name']?.toString() ?? '',
      value: fluidType.id as number,
    }));
  }

  @computed
  get wellTypeOptions(): TOption[] {
    return this.wellsService.wellTypes.map((wellType) => ({
      label: wellType.data['abbreviatedName']?.toString() ?? '',
      value: wellType.id as number,
    }));
  }

  @computed
  get coreSamplingIntervalOptions(): TOption[] {
    return this.wellsService.coreSamplingIntervals.map((coreSamplingInterval) => ({
      label: coreSamplingInterval.data['name']?.toString() ?? '',
      value: coreSamplingInterval.id as number,
    }));
  }

  @computed
  get clusterOptions(): TOption[] {
    return this.wellsService.clusters.map((cluster) => ({
      label: cluster.data['name']?.toString() ?? '',
      value: cluster.id as number,
    }));
  }

  @computed
  get reservoirOptions(): TOption[] {
    return this.wellsService.reservoirs.map((reservoir) => ({
      label: reservoir.data['name']?.toString() ?? '',
      value: reservoir.id as number,
    }));
  }

  @computed
  get drillingStatusOptions(): TOption[] {
    return this.wellsService.drillingStatuses.map((drillingStatus) => ({
      label: drillingStatus.data['name']?.toString() ?? '',
      value: drillingStatus.id as number,
    }));
  }

  @computed
  get wellPurposeOptions(): TOption[] {
    return this.wellsService.wellPurposes.map((wellPurpose) => ({
      label: wellPurpose.data['name']?.toString() ?? '',
      value: wellPurpose.id as number,
    }));
  }

  @computed
  get wellEndingTypeOptions(): TOption[] {
    return this.wellsService.wellEndingTypes.map((wellEndingType) => ({
      label: wellEndingType.data['abbreviatedName']?.toString() ?? '',
      value: wellEndingType.id as number,
    }));
  }

  @computed
  get wellCategoryOptions(): TOption[] {
    return this.wellsService.wellCategories.map((wellCategory) => ({
      label: wellCategory.data['name']?.toString() ?? '',
      value: wellCategory.id as number,
    }));
  }

  @computed
  get rigOptions(): TOption[] {
    const rigModels = this.wellsService.rigModels;
    const rigModelsMap = new Map<number, string>(
      rigModels.map((rigModel) => [rigModel.id as number, rigModel.data['name']?.toString() ?? '']),
    );

    return this.wellsService.rigs.map((rig) => {
      const modelName = rigModelsMap.get(rig.data['modelId'] as number) ?? '';
      const rugNumber = rig.data['rigNumber']?.toString() ?? '';

      const label = modelName ? `${modelName} / ${rugNumber}` : rugNumber;

      return {
        label,
        value: rig.id as number,
      };
    });
  }

  @computed
  get brigadeOptions(): TOption[] {
    return this.wellsService.brigades.map((brigade) => ({
      label: brigade.data['name']?.toString() ?? '',
      value: brigade.id as number,
    }));
  }

  @computed
  get padTypeOptions(): TOption[] {
    return this.wellsService.padTypes.map((padType) => ({
      label: padType.data['name']?.toString() ?? '',
      value: padType.id as number,
    }));
  }

  @computed
  get passageTypeOptions(): TOption[] {
    return this.wellsService.passageTypes.map((passageType) => ({
      label: passageType.data['name']?.toString() ?? '',
      value: passageType.id as number,
    }));
  }

  @computed
  get wellLifeCycleStageOptions(): TOption[] {
    return this.wellsService.wellLifeCycleStages.map((wellLifeCycleStage) => ({
      label: wellLifeCycleStage.data['name']?.toString() ?? '',
      value: wellLifeCycleStage.id as number,
    }));
  }

  @computed
  get sectionTypeOptions(): TOption[] {
    return this.wellsService.sectionTypes.map((sectionType) => ({
      label: sectionType.data['name']?.toString() ?? '',
      value: sectionType.id as number,
    }));
  }

  @computed
  get directoriesObjects() {
    return {
      blocks: this.wellsService.blocks,
      fields: this.wellsService.fields,
      organizations: this.wellsService.organizations,
      pads: this.wellsService.pads,
      fluidTypes: this.wellsService.fluidTypes,
      wellTypes: this.wellsService.wellTypes,
      coreSamplingIntervals: this.wellsService.coreSamplingIntervals,
      clusters: this.wellsService.clusters,
      reservoirs: this.wellsService.reservoirs,
      drillingStatuses: this.wellsService.drillingStatuses,
      wellPurposes: this.wellsService.wellPurposes,
      wellEndingTypes: this.wellsService.wellEndingTypes,
      wellCategories: this.wellsService.wellCategories,
      rigs: this.wellsService.rigs,
      brigades: this.wellsService.brigades,
      padTypes: this.wellsService.padTypes,
      passageTypes: this.wellsService.passageTypes,
      rigModels: this.wellsService.rigModels,
      wellLifeCycleStages: this.wellsService.wellLifeCycleStages,
      sectionTypes: this.wellsService.sectionTypes,
    };
  }

  @computed
  get rows(): TheWellEntity[] {
    return this.wellsService.theWells;
  }

  @computed
  get getClusterIdFn(): (blockId: number) => number | null {
    const blockFieldLinksMap = new Map<number, number>(
      this.wellsService.blockFieldLinks.map((blockFieldLink) => [
        blockFieldLink.data['blockId'] as number,
        blockFieldLink.data['clusterId'] as number,
      ]),
    );

    return (blockId: number) => {
      return blockFieldLinksMap.get(blockId) ?? null;
    };
  }

  @computed
  get isWellsUpdating(): boolean {
    return this.wellsService.isWellsUpdating;
  }

  private onWellsSelectionChange(model: GridRowSelectionModel): void {
    if (!model.length) {
      this.wellsPageStore.setIsDeleteButtonDisabled(true);
      return;
    }

    const theWells = model.map((id) => this.wellsService.theWellsMap.get(id as string)).filter(hasValue);

    if (!theWells.length) {
      this.wellsPageStore.setIsDeleteButtonDisabled(true);
      return;
    }

    const isOnlyUnplannedWells = theWells.every((well) => isWellUnplanned(well, this.directories));

    if (isOnlyUnplannedWells) {
      this.wellsPageStore.setIsDeleteButtonDisabled(false);
    } else {
      this.wellsPageStore.setIsDeleteButtonDisabled({
        disabled: true,
        description: i18n.t('wells:ControlBar.deleteWellsDescription'),
      });
    }
  }

  @action.bound
  setSelectedRows(model: GridRowSelectionModel): void {
    this.selectedRows = model;
    this.onWellsSelectionChange(model);
  }

  private setInitialTableFilterValues(filterModel?: GridFilterModel) {
    if (!filterModel) {
      return;
    }

    const filterData = filterModel.quickFilterValues?.reduce(
      (acc, val) => {
        try {
          const data = JSON.parse(val) as {
            startDate?: number | null;
            endDate?: number | null;
            isDrilledWellsChecked?: boolean;
            isPlannedWellsChecked?: boolean;
            isUnplannedWellsChecked?: boolean;
          };

          return data;
        } catch (e) {
          console.error(e);
          return acc;
        }
      },
      {} as Record<string, boolean>,
    );

    this.wellsPageStore.setStartDate(filterData?.startDate ?? null);
    this.wellsPageStore.setEndDate(filterData?.endDate ?? null);
    this.wellsPageStore.setIsDrilledWellsChecked(filterData?.isDrilledWellsChecked ?? true);
    this.wellsPageStore.setIsPlannedWellsChecked(filterData?.isPlannedWellsChecked ?? true);
    this.wellsPageStore.setIsUnplannedWellsChecked(filterData?.isUnplannedWellsChecked ?? true);
  }

  @flow.bound
  private async *loadTableSettings() {
    try {
      const settings = await this.userSettingsService.getSettings<TTableSettingsModel>(
        'wells',
        DEFAULT_TABLE_SETTINGS_MODEL,
      );
      yield;

      this.displayManager = new DisplayManager(settings, this.userSettingsService, this.notifications);

      this.setInitialTableFilterValues(this.displayManager.settings.filterModel);
    } catch (e) {
      console.error(e);
    }
  }

  getRecordById(id: string): TheWellEntity | null {
    return this.wellsService.theWellsMap.get(id) ?? null;
  }

  @action.bound
  setDefaultColumnsOrderModel(columnsOrderModel: string[]): void {
    this.defaultColumnsOrderModel = columnsOrderModel;
  }

  export = async (
    _tableSettings?: Omit<TTableSettingsModel, 'ownerUserId'> | 'currentTableSettings',
  ): Promise<void> => {
    const state = this.apiRef.current?.exportState();

    if (!state) {
      console.error('state is not presented');
      return;
    }

    const rows = this.apiRef.current?.getSortedRows() ?? [];
    const initialColumns = this.apiRef.current?.getAllColumns() ?? [];

    this.tableApiRefForExport.current?.restoreState(state);

    this.tableApiRefForExport.current?.updateColumns(initialColumns);
    this.tableApiRefForExport.current?.updateRows(rows);

    const columns = this.tableApiRefForExport.current?.getAllColumns() ?? [];

    const tableSettings =
      _tableSettings === 'currentTableSettings' ? toJS(this.displayManager?.settings) : _tableSettings;

    if (tableSettings && this.displayManager) {
      applySettingsToTable(this.tableApiRefForExport, tableSettings, this.displayManager.columnsGrouping, {
        defaultColumnsOrderModel: this.defaultColumnsOrderModel,
      });
    }

    const worker = new Worker(new URL('./wells-export.worker.ts', import.meta.url), { type: 'module' });

    const filterData = tableSettings?.filterModel?.quickFilterValues?.reduce(
      (acc, val) => {
        try {
          const data = JSON.parse(val) as {
            startDate?: number | null;
            endDate?: number | null;
            isDrilledWellsChecked?: boolean;
            isPlannedWellsChecked?: boolean;
            isUnplannedWellsChecked?: boolean;
          };

          return data;
        } catch (e) {
          console.error(e);
          return acc;
        }
      },
      {} as Record<string, boolean>,
    );

    const startDate = filterData ? (filterData.startDate ?? null) : this.wellsPageStore.startDate;
    const endDate = filterData ? (filterData.endDate ?? null) : this.wellsPageStore.endDate;
    const drilledWells = filterData?.isDrilledWellsChecked ?? this.wellsPageStore.isDrilledWellsChecked;
    const plannedWells = filterData?.isPlannedWellsChecked ?? this.wellsPageStore.isPlannedWellsChecked;
    const unplannedWells = filterData?.isUnplannedWellsChecked ?? this.wellsPageStore.isUnplannedWellsChecked;

    worker.postMessage({
      namespace: 'prepare-export',
      exportSettings: {
        ...toJS(tableSettings),
        columnsData: JSON.parse(JSON.stringify(columns)),
        groupingData: this.tableApiRefForExport.current?.getAllGroupDetails(),
        unwrappedGroupingModel: this.tableApiRefForExport.current?.state.columnGrouping.unwrappedGroupingModel,
        dateInterval: [
          startDate ? format(fromUnixTime(startDate), 'dd.MM.yyyy') : '-',
          endDate ? format(fromUnixTime(endDate), 'dd.MM.yyyy') : '-',
        ],
        drilledWells: drilledWells ? i18n.t('common:Buttons.yes') : i18n.t('common:Buttons.no'),
        plannedWells: plannedWells ? i18n.t('common:Buttons.yes') : i18n.t('common:Buttons.no'),
        unplannedWells: unplannedWells ? i18n.t('common:Buttons.yes') : i18n.t('common:Buttons.no'),
        planVersion:
          this.planVersionService.currentVersion?.data.name.replaceAll(/[\*\?\:\\\/\[\]]/g, '_') ??
          i18n.t('wells:unknownVersion'),
        settingsLabels: {
          worksheetName: i18n.t('wells:ExportSettings.worksheetName'),
          dateRange: i18n.t('wells:ExportSettings.dateRange'),
          drilled: i18n.t('wells:ExportSettings.drilled'),
          planned: i18n.t('wells:ExportSettings.planned'),
          unplanned: i18n.t('wells:ExportSettings.unplanned'),
        },
      },
    });

    const exportSettingsPrepared = createPromiseController<boolean>();

    worker.addEventListener('message', (event) => {
      if (event.data.namespace === 'export-settings-prepared') {
        exportSettingsPrepared.resolve(true);
      }
    });

    await exportSettingsPrepared;
    await this.tableApiRefForExport.current?.exportDataAsExcel({
      fileName: this.planVersionService.currentVersion?.data.name ?? i18n.t('wells:unknownVersion'),
      includeColumnGroupsHeaders: true,
      worker: () => worker,
    });
  };

  private setTableApiRefSubscribers = () => {
    let timeoutId: NodeJS.Timeout | null = null;
    const disposers: { disposers: VoidFunction[] } = { disposers: [] };

    const checkInstanceId = () => {
      const id = this.tableApiRefForExport.current?.instanceId?.id;

      if (hasValue(id)) {
        if (timeoutId) {
          clearInterval(timeoutId);
        }

        const exportDisposer = this.tableApiRefForExport.current.subscribeEvent('excelExportStateChange', (state) => {
          runInAction(() => {
            if (state === 'finished') {
              this.wellsPageStore.setIsExporting(false);
            } else {
              this.wellsPageStore.setIsExporting(true);
            }
          });
        });

        disposers.disposers = [exportDisposer];
      }
    };

    timeoutId = setInterval(checkInstanceId, 300);

    return () => {
      if (timeoutId) {
        clearInterval(timeoutId);
      }

      disposers.disposers.forEach((disposer) => disposer());
    };
  };

  init = (): VoidFunction => {
    this.loadTableSettings();

    const tableApiRefSubscribersDisposer = this.setTableApiRefSubscribers();

    const deleteWellsDisposer = this.wellsPageStore.eventBus.subscribe('deleteWells', async () => {
      const isDeleted = await this.wellsService.deleteWells(this.selectedRows.map((id) => id as string));

      if (isDeleted) {
        this.apiRef.current?.setRowSelectionModel([]);
        this.apiRef.current?.updateRows(this.selectedRows.map((id) => ({ id, _action: 'delete' })));
        this.setSelectedRows([]);
      }
    });

    const headerFiltersDisposer = reaction(
      () => ({
        isDrilledWellsChecked: this.wellsPageStore.isDrilledWellsChecked,
        isPlannedWellsChecked: this.wellsPageStore.isPlannedWellsChecked,
        isUnplannedWellsChecked: this.wellsPageStore.isUnplannedWellsChecked,
        startDate: this.wellsPageStore.startDate,
        endDate: this.wellsPageStore.endDate,
      }),
      ({ isDrilledWellsChecked, isPlannedWellsChecked, isUnplannedWellsChecked, startDate, endDate }) => {
        const filterData = {
          startDate,
          endDate,
          isDrilledWellsChecked: isDrilledWellsChecked,
          isPlannedWellsChecked: isPlannedWellsChecked,
          isUnplannedWellsChecked: isUnplannedWellsChecked,
        };

        this.apiRef.current?.setQuickFilterValues?.([JSON.stringify(filterData)]);
      },
    );

    return () => {
      headerFiltersDisposer();
      tableApiRefSubscribersDisposer();
      deleteWellsDisposer();
      this.wellsPageStore.setIsExporting(false);
    };
  };
}
